export type ServerUtcInput = string | number | Date | null | undefined;

type NumberUnit = 'auto' | 'seconds' | 'milliseconds';

const MILLIS_THRESHOLD = 1_000_000_000_000;

const toEpochMillis = (value: number, numberUnit: NumberUnit): number => {
  if (numberUnit === 'seconds') return value * 1000;
  if (numberUnit === 'milliseconds') return value;
  return Math.abs(value) < MILLIS_THRESHOLD ? value * 1000 : value;
};

const parseNumericString = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

export function parseServerUtcToLocalDate(
  input: ServerUtcInput,
  numberUnit: NumberUnit = 'auto'
): Date | null {
  if (input == null || input === '') return null;

  if (input instanceof Date) {
    return Number.isNaN(input.getTime()) ? null : new Date(input.getTime());
  }

  if (typeof input === 'number') {
    const date = new Date(toEpochMillis(input, numberUnit));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const numericString = parseNumericString(input);
  const date =
    numericString !== null ? new Date(toEpochMillis(numericString, numberUnit)) : new Date(input);

  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatServerUtcToLocal(
  input: ServerUtcInput,
  {
    locale = 'ja-JP',
    numberUnit = 'auto',
    invalidValue = 'NaN/NaN/NaN NaN:NaN:NaN',
    options,
  }: {
    locale?: string;
    numberUnit?: NumberUnit;
    invalidValue?: string;
    options?: Intl.DateTimeFormatOptions;
  } = {}
): string {
  const date = parseServerUtcToLocalDate(input, numberUnit);
  if (!date) return invalidValue;

  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    ...options,
  }).format(date);
}
